import React, { useState, useCallback } from 'react';
import { PenLine, X, Send, RefreshCw, FileCheck, AlertTriangle, Loader, Plus, Trash2, CheckCircle2, Clock } from 'lucide-react';
import { signaturesApi } from '../../utils/api';

const STATUT = {
  envoye: { label: 'En attente de signature', pill: 'bg-amber-50 text-amber-700' },
  signe: { label: 'Signé', pill: 'bg-emerald-50 text-emerald-600' },
  refuse: { label: 'Refusé / annulé', pill: 'bg-red-50 text-red-600' },
};
const ROLE_LABEL = { parent1: 'Parent 1', parent2: 'Parent 2', gestionnaire: 'La crèche', autre: 'Signataire' };

export default function SignatureButton({ type, refId, email, nom, compact }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null); // { configured, groupes, suggestions }
  const [busy, setBusy] = useState(false);
  const [signers, setSigners] = useState([]);
  const [nouvelle, setNouvelle] = useState(false); // forcer le formulaire d'envoi
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await signaturesApi.getStatus(type, refId);
      setData(data);
      // Pré-remplit les signataires depuis les suggestions (ceux qui ont un e-mail)
      const sugg = (data.suggestions || []).filter(s => s.email);
      setSigners(sugg.length ? sugg : [{ role: 'parent1', nom: nom || '', email: email || '' }]);
    } catch { setData({ configured: false, groupes: [], suggestions: [] }); }
  }, [type, refId, email, nom]);

  const openModal = () => { setMsg(null); setNouvelle(false); setOpen(true); setData(null); load(); };

  const groupe = data?.groupes?.[0];
  const showForm = nouvelle || !groupe;

  const setSigner = (i, patch) => setSigners(list => list.map((s, j) => j === i ? { ...s, ...patch } : s));
  const addSigner = () => setSigners(list => [...list, { role: 'autre', nom: '', email: '' }]);
  const removeSigner = (i) => setSigners(list => list.filter((_, j) => j !== i));

  const envoyer = async () => {
    const liste = signers.map(s => ({ ...s, email: (s.email || '').trim(), nom: (s.nom || '').trim() })).filter(s => s.email);
    if (!liste.length) { setMsg({ type: 'error', text: 'Renseignez au moins un e-mail de signataire.' }); return; }
    setBusy(true); setMsg(null);
    try {
      const { data: r } = await signaturesApi.envoyer({ type, ref_id: refId, signataires: liste });
      setMsg({ type: 'ok', text: r.message || 'Document envoyé pour signature.' });
      setNouvelle(false);
      await load();
    } catch (err) { setMsg({ type: 'error', text: err.response?.data?.error || 'Erreur' }); }
    setBusy(false);
  };
  const actualiser = async () => { setBusy(true); setMsg(null); await load(); setBusy(false); };
  const sync = async (sigId) => {
    setBusy(true); setMsg(null);
    try { await signaturesApi.sync(sigId); await load(); }
    catch (err) { setMsg({ type: 'error', text: err.response?.data?.error || 'Erreur' }); }
    setBusy(false);
  };

  return (
    <>
      {compact ? (
        <button onClick={openModal} title="Signature électronique" className="p-1.5 rounded hover:bg-gold-50 text-gray-500 hover:text-gold-600">
          <PenLine size={16} />
        </button>
      ) : (
        <button onClick={openModal} className="apple-btn-secondary"><PenLine size={15} /> Signature électronique</button>
      )}

      {open && (
        <div className="fixed inset-0 bg-navy-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-backdrop" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-[22px] shadow-2xl w-full max-w-md animate-sheet max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 pt-5 pb-3 sticky top-0 bg-white">
              <h3 className="font-serif text-[20px] text-navy-900">Signature électronique</h3>
              <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full bg-navy-50 hover:bg-navy-100 flex items-center justify-center text-navy-400"><X size={17} /></button>
            </div>
            <div className="px-6 pb-6 space-y-3">
              {!data ? (
                <p className="text-navy-300 text-sm py-4 text-center">Chargement…</p>
              ) : !data.configured ? (
                <div className="text-[13px] text-amber-700 bg-amber-50 rounded-xl px-4 py-3 flex items-start gap-2">
                  <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>L'envoi d'e-mails n'est pas configuré (SMTP). Impossible d'envoyer le document en signature pour le moment.</span>
                </div>
              ) : !showForm ? (
                /* ─── Suivi du groupe de signature en cours ─── */
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-[12px] px-2.5 py-1 rounded-full font-medium ${(STATUT[groupe.statut_global] || STATUT.envoye).pill}`}>
                      {(STATUT[groupe.statut_global] || STATUT.envoye).label}
                    </span>
                    <span className="text-[12.5px] text-navy-400">{groupe.signes}/{groupe.total} signé{groupe.signes > 1 ? 's' : ''}</span>
                  </div>

                  <div className="rounded-xl border border-navy-100 divide-y divide-navy-50">
                    {groupe.signataires.map(s => (
                      <div key={s.id} className="flex items-center gap-2.5 px-3 py-2">
                        {s.statut === 'signe'
                          ? <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0" />
                          : <Clock size={16} className="text-amber-500 flex-shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] text-navy-800 truncate">{s.nom || s.email}</div>
                          <div className="text-[11.5px] text-navy-400 truncate">{ROLE_LABEL[s.role] || 'Signataire'} · {s.email}</div>
                        </div>
                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${(STATUT[s.statut] || STATUT.envoye).pill}`}>
                          {s.statut === 'signe' ? 'Signé' : 'En attente'}
                        </span>
                      </div>
                    ))}
                  </div>

                  {groupe.statut_global === 'signe' && groupe.signed_pdf_id ? (
                    <a href={signaturesApi.getDocumentUrl(groupe.signed_pdf_id)} target="_blank" rel="noopener noreferrer" className="apple-btn-primary w-full justify-center">
                      <FileCheck size={16} /> Voir le document signé{groupe.completed_at ? ` (${groupe.completed_at})` : ''}
                    </a>
                  ) : (
                    <>
                      <p className="text-[12.5px] text-navy-400">Chaque signataire a reçu un e-mail avec un lien sécurisé. Le document signé se déposera automatiquement dans le dossier de l'enfant une fois signé par tous.</p>
                      <button onClick={groupe.provider === 'docusign' ? () => sync(groupe.signataires[0].id) : actualiser} disabled={busy} className="apple-btn-secondary w-full justify-center">
                        {busy ? <Loader size={15} className="animate-spin" /> : <RefreshCw size={15} />} Actualiser le statut
                      </button>
                    </>
                  )}
                  <button onClick={() => { setNouvelle(true); setMsg(null); }} className="apple-btn-ghost w-full justify-center text-[13px]">Nouvelle demande de signature</button>
                </div>
              ) : (
                /* ─── Formulaire d'envoi multi-signataires ─── */
                <div className="space-y-3">
                  <p className="text-[13px] text-navy-500">Chaque signataire recevra son propre e-mail avec un lien sécurisé. Le document sera signé en ligne (vérification par code) et déposé dans le dossier de l'enfant une fois que <strong>tous</strong> auront signé.</p>

                  {signers.map((s, i) => (
                    <div key={i} className="rounded-xl border border-navy-100 p-3 space-y-2 relative">
                      <div className="flex items-center justify-between">
                        <span className="text-[11.5px] font-semibold text-navy-500 uppercase tracking-wide">{ROLE_LABEL[s.role] || 'Signataire'}</span>
                        {signers.length > 1 && (
                          <button onClick={() => removeSigner(i)} className="text-navy-300 hover:text-red-500" title="Retirer"><Trash2 size={14} /></button>
                        )}
                      </div>
                      <input className="form-input rounded-lg text-[13px]" value={s.email} onChange={e => setSigner(i, { email: e.target.value })} placeholder="email@exemple.fr" />
                      <input className="form-input rounded-lg text-[13px]" value={s.nom} onChange={e => setSigner(i, { nom: e.target.value })} placeholder="Prénom Nom" />
                    </div>
                  ))}

                  <button onClick={addSigner} className="apple-btn-ghost w-full justify-center text-[13px]"><Plus size={14} /> Ajouter un signataire</button>

                  <button onClick={envoyer} disabled={busy || !signers.some(s => (s.email || '').trim())} className="apple-btn-gold w-full justify-center">
                    {busy ? <Loader size={15} className="animate-spin" /> : <Send size={15} />} Envoyer en signature
                  </button>
                  {groupe && <button onClick={() => { setNouvelle(false); setMsg(null); }} className="apple-btn-ghost w-full justify-center text-[13px]">Annuler</button>}
                </div>
              )}
              {msg && <div className={`text-[13px] rounded-lg px-3 py-2 ${msg.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>{msg.text}</div>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
